import uniq from 'lodash/uniq';
import some from 'lodash/some';

import {SegmentFilterController} from 'common/charts/contexts/segment-filter-controller';
import {Section} from 'common/components/items-menu/types';
import {
  ChartType,
  PRIMITIVE_MATH_TYPES,
  RESULTS_OVERALL_SUM_KEY,
  ValueRepresentationType,
} from 'common/report/constants';
import {getTimeSection} from 'common/report/insights/util/time';
import {
  AnalysisType,
  ChartPlotStyle,
  DisplayOptions,
  InsightsBookmarkParams,
  InsightsBookmarkSections,
  InsightsLegend,
  InsightsQueryResults,
} from 'common/report/insights/util/types';
import {generateCohortBaselineOptions} from 'common/report/util/lift';
import {LiftType, SegmentLiftBaselineOption} from 'common/report/util/lift/types';
import {getDisplayOptions, getSupportedTimeUnits} from 'common/report/insights/util';
import {capitalize, defaultRollingWindowSize, getOwnAttr} from 'common/util';
import {ChartHeaderUpdateDetail} from 'common/widgets/chart-header';
import {TOP_PEOPLE} from 'common/query-builder/query-block/properties-menu/constants';
import {isCohortSegment, Segment} from 'common/query-builder/query-block/segment-menu/types';
import {comparisonDateToBookmarkFormat} from 'common/query-builder/query-block/time-comparison-menu/utils';
import {FeatureRolloutName, FeatureRollouts} from 'common/report/util/feature-sampling';
import {CurrentProject} from 'common/report/model-resources/projects';

import {ROLLING_WINDOW_SIZE_OPTIONS, TIME_COMPARISON_DISABLED_REASONS} from '../models/constants';
import {Report} from '../models/report';
import {getKeysAtDepth} from '../util';
import {isFlattenedData} from '../util/chart';
import {ANALYSIS_TYPE_TOOLTIPS} from './constants';

const NON_LINE_CHART_TYPES = new Set([ChartType.Bar, ChartType.Pie, ChartType.Table, ChartType.Metric]);
const NON_QUERYABLE_TYPE_CHANGES = new Set([AnalysisType.Linear, AnalysisType.Logarithmic]);

export function getAnalysisMenuItems({
  report,
  currentProject,
}: {
  report: Report;
  currentProject: CurrentProject;
}): Section[] {
  const chartType = getSelectedChartType(report);
  switch (chartType) {
    case ChartType.Pie:
    case ChartType.Table:
    case ChartType.Metric:
      return null;
  }

  return [
    {
      items: Object.values(AnalysisType)
        .filter((analysis) => {
          if (FeatureRollouts[FeatureRolloutName.InsightsNewCountMenu].isEnabled(currentProject)) {
            return analysis !== AnalysisType.Rolling && analysis !== AnalysisType.Cumulative;
          }
          return true;
        })
        .map((analysis) => ({
          value: analysis,
          label: capitalize(analysis),
          icon: `analysis-${analysis}`,
          isSelected: analysis === report.getDisplayAnalysis(),
          isDisabled: !isAnalysisEnabled(report.displayOptions, analysis),
          tooltip: ANALYSIS_TYPE_TOOLTIPS[analysis][chartType],
        })),
    },
  ];
}

export function getSelectedAnalysis(report: Report): string {
  return capitalize(report.getDisplayAnalysis());
}

export function getSelectedAnalysisIcon(report: Report): string {
  return `analysis-${report.getDisplayAnalysis()}`;
}

export function getSelectedChartType(report: Report): ChartType {
  return toCombinedChartType(report.displayOptions.chartType, report.displayOptions.plotStyle);
}

export function getSelectedRollingOption(report: Report): string {
  return `Rolling Interval: ${report.displayOptions.rollingWindowSize} ${report.sections.time.clauses[0].unit}s`;
}

export function getRollingOptions(report: Report): Section[] {
  const currentWindowSize = report.displayOptions.rollingWindowSize;
  const selectedTimeUnit = report.sections.time.clauses[0].unit;

  return [
    {
      items: ROLLING_WINDOW_SIZE_OPTIONS[selectedTimeUnit].map((size) => ({
        label: `${size} ${selectedTimeUnit}s`,
        value: size,
        isSelected: size === currentWindowSize,
      })),
    },
  ];
}

export function getTimeComparisonDisabledReason(report: Report, {displayOptionsOverrides = {}} = {}): string {
  const displayOptions = {...report.displayOptions, ...displayOptionsOverrides};
  if (displayOptions.plotStyle === ChartPlotStyle.Stacked && displayOptions.chartType !== ChartType.Bar) {
    return TIME_COMPARISON_DISABLED_REASONS.STACKED_CHART;
  }
  if (report.sections.show.isPeopleOnlyQuery()) {
    return TIME_COMPARISON_DISABLED_REASONS.PEOPLE_QUERY;
  }
  if (displayOptions.chartType === ChartType.Pie) {
    return TIME_COMPARISON_DISABLED_REASONS.PIE_CHART;
  }
  return null;
}

export function getTimeComparisonDisabledReasonMessage(report: Report, reason): string {
  switch (reason) {
    case TIME_COMPARISON_DISABLED_REASONS.PEOPLE_QUERY: {
      return `Time comparison is not available for profile queries`;
    }
    case TIME_COMPARISON_DISABLED_REASONS.STACKED_CHART: {
      const chartType = report.displayOptions.chartType;
      return `Time comparison is not available for stacked ${chartType} chart`;
    }
    case TIME_COMPARISON_DISABLED_REASONS.PIE_CHART: {
      return `Time comparison is not available for pie chart`;
    }
    default:
      return ``;
  }
}

export function toCombinedChartType(chartType, plotStyle): ChartType {
  if (plotStyle === ChartPlotStyle.Stacked) {
    if (chartType === ChartType.Line) {
      return ChartType.StackedLine;
    } else if (chartType === ChartType.Bar) {
      return ChartType.StackedBar;
    }
  }

  return chartType;
}

function isAnalysisEnabled(displayOptions: DisplayOptions, analysisType: AnalysisType): boolean {
  let supportedAnalyses: AnalysisType[];

  switch (displayOptions.chartType) {
    case ChartType.Line:
      supportedAnalyses = Object.values(AnalysisType);
      break;
    case ChartType.Bar:
      supportedAnalyses = [AnalysisType.Linear, AnalysisType.Logarithmic];
      break;
    default:
      supportedAnalyses = [AnalysisType.Linear];
      break;
  }

  return supportedAnalyses.includes(analysisType);
}

function isValueToggleEnabled(displayOptions: DisplayOptions): boolean {
  switch (displayOptions.chartType) {
    case ChartType.Bar:
    case ChartType.Table:
      return true;
    case ChartType.Line:
      return displayOptions.plotStyle === ChartPlotStyle.Stacked;
    default:
      return false;
  }
}

export function correctDisplayOptions(options): DisplayOptions {
  const displayOptions = {
    ...options,
    analysis: isAnalysisEnabled(options, options.analysis) ? options.analysis : AnalysisType.Linear,
    value: isValueToggleEnabled(options) ? options.value : ValueRepresentationType.Absolute,
  };

  if (displayOptions.analysis !== AnalysisType.Rolling) {
    delete displayOptions.rollingWindowSize;
  }

  return displayOptions;
}

export function bookmarkUpdatesFromAnalysisChange(
  params: InsightsBookmarkParams,
  analysis: AnalysisType,
): Pick<InsightsBookmarkParams, 'displayOptions'> {
  const displayOptions = {...params.displayOptions, analysis};

  // Set default rollingWindowSize when rollingWindowSize is not set (i.e. analysis wasn't rolling, old bookmark)
  if (analysis === AnalysisType.Rolling && params.displayOptions.analysis !== AnalysisType.Rolling) {
    displayOptions.rollingWindowSize = defaultRollingWindowSize(params.sections.time[0].unit as any);
  }

  return {displayOptions};
}

export function bookmarkUpdatesFromHeaderUpdate({
  report,
  currentProject,
  update,
  enableLiftComparison,
  hasChangedTimeUnit,
}: {
  report: Report;
  currentProject: CurrentProject;
  update: ChartHeaderUpdateDetail;
  enableLiftComparison: boolean;
  hasChangedTimeUnit: boolean;
}): Partial<InsightsBookmarkParams> {
  const bookmarkUpdates: Partial<InsightsBookmarkParams> = {};
  const oldTimeUnit = report.sections.time.clauses[0].unit;

  const changedTimeUnit = getOwnAttr(update.delta, `dateUnit`);

  if (update.delta.dateRange || changedTimeUnit) {
    const modelSections = report.sections.replaceSection(
      getTimeSection(update.dateRange, {
        selectedTimeUnit: report.sections.time.clauses[0].unit,
        overrideTimeUnit: changedTimeUnit,
        // GROSS statefulness
        // use default time unit only when the user has not
        // interacted with the time unit dropdown before
        useDefaultTimeUnit: !hasChangedTimeUnit,
      }),
    );

    const bookmarkSections: InsightsBookmarkSections = modelSections.toBookmarkData();
    const newTimeClause = bookmarkSections.time[0];

    // reset time granularity for invalid time units
    const supportedTimeUnits = getSupportedTimeUnits({...report.serialize(), sections: bookmarkSections});
    if (!supportedTimeUnits.includes(newTimeClause.unit)) {
      newTimeClause.unit = supportedTimeUnits[0];
    }

    bookmarkUpdates.sections = bookmarkSections;
  }

  if (`comparisonDateRange` in update.delta) {
    if (update.delta.comparisonDateRange) {
      bookmarkUpdates.timeComparison = comparisonDateToBookmarkFormat(update.delta.comparisonDateRange);
    } else {
      bookmarkUpdates.timeComparison = null;
    }
  }

  if (enableLiftComparison && `liftComparison` in update.delta) {
    bookmarkUpdates.liftComparison = update.delta.liftComparison;
  }

  if (update.delta.chartType) {
    let chartType = update.delta.chartType;
    let plotStyle = ChartPlotStyle.Standard;

    if (chartType === ChartType.StackedLine) {
      chartType = ChartType.Line;
      plotStyle = ChartPlotStyle.Stacked;
    } else if (chartType === ChartType.StackedBar) {
      chartType = ChartType.Bar;
      plotStyle = ChartPlotStyle.Stacked;
    }

    const liftType = report.liftComparison?.type;
    // we don't support stacked line or bar for baseline & compare to past lift, so disable if we switch to those
    // but compare to overall is fine with these stacked charts
    if (
      liftType !== LiftType.Overall &&
      plotStyle === ChartPlotStyle.Stacked &&
      (chartType === ChartType.Bar || chartType === ChartType.Line)
    ) {
      report.liftComparison = null;
    }

    bookmarkUpdates.displayOptions = correctDisplayOptions({
      ...report.displayOptions,
      chartType,
      plotStyle,
    });
  }

  if (update.delta.valueRepresentation) {
    bookmarkUpdates.displayOptions = correctDisplayOptions({
      ...report.displayOptions,
      value: update.delta.valueRepresentation,
    });
  }

  // Reset default rollingWindowSize when changing time unit
  if (
    FeatureRollouts[FeatureRolloutName.InsightsNewCountMenu].isEnabled(currentProject) &&
    changedTimeUnit &&
    oldTimeUnit !== changedTimeUnit
  ) {
    const showClauses = [...report.sections.show.clauses];
    bookmarkUpdates.sections.show = showClauses.map((showClause) => {
      return {
        ...showClause,
        ...(showClause.rolling && {
          rolling: {
            rollingWindowSize: defaultRollingWindowSize(changedTimeUnit),
          },
        }),
      };
    });
  }
  if (report.displayOptions.analysis === AnalysisType.Rolling && changedTimeUnit && oldTimeUnit !== changedTimeUnit) {
    bookmarkUpdates.displayOptions = correctDisplayOptions({
      ...report.displayOptions,
      rollingWindowSize: defaultRollingWindowSize(changedTimeUnit),
    });
  }

  return bookmarkUpdates;
}

export function shouldRequeryDisplayOptions(
  oldDisplayOptions: DisplayOptions,
  newDisplayOptions: DisplayOptions,
): boolean {
  const chartType = newDisplayOptions.chartType;
  const oldChartType = oldDisplayOptions.chartType;
  const oldAnalysis = oldDisplayOptions.analysis;
  const analysis = newDisplayOptions.analysis;

  const isChangingToLineChart = chartType === ChartType.Line && NON_LINE_CHART_TYPES.has(oldChartType);
  const isChangingFromLineChart = NON_LINE_CHART_TYPES.has(chartType) && oldChartType === ChartType.Line;

  // only case where we don't need to re-query is changing between from line to logarithmic view
  const isChangingFromLineToLog =
    NON_QUERYABLE_TYPE_CHANGES.has(analysis) && NON_QUERYABLE_TYPE_CHANGES.has(oldAnalysis);

  const isChangingRollingWindowSize = newDisplayOptions.rollingWindowSize !== oldDisplayOptions.rollingWindowSize;

  return (
    isChangingToLineChart || isChangingFromLineChart || isChangingFromLineToLog === false || isChangingRollingWindowSize
  );
}

/**
 * React to SegmentFilterController changes by returning a modified bookmark.
 * Used by the component which provides SegmentFilterController in order to persist selections.
 */
export function handleSegmentFilterControllerChange(
  segmentFilterController: SegmentFilterController,
  params: InsightsBookmarkParams,
): InsightsBookmarkParams {
  if (segmentFilterController.hasLoaded()) {
    const savedLegend = segmentFilterController.toBookmarkSavedLegend();
    const chartType = getDisplayOptions(params.sections, params.displayOptions).chartType;

    const isFlat = isFlattenedData(chartType);

    let legend: InsightsLegend;
    if (isFlat) {
      legend = {individualSelectedSegments: savedLegend[0]};
    } else {
      legend = {filteredBreakdownSegments: savedLegend};
    }

    return {...params, legend};
  }

  return params;
}

// lift

export function getLeafLevelSegments({
  breakdowns,
  queryResults,
  report,
}: {
  breakdowns: Segment[];
  queryResults: InsightsQueryResults;
  report: Report;
}): SegmentLiftBaselineOption[] {
  if (queryResults) {
    let searchDepth = 0;
    if (breakdowns.length) {
      const lastBreakdown = breakdowns[breakdowns.length - 1];
      if (isCohortSegment(lastBreakdown)) {
        return generateCohortBaselineOptions(lastBreakdown);
      } else {
        searchDepth = breakdowns.length;
      }
    } else if (some(report.sections.show.clauses, {math: PRIMITIVE_MATH_TYPES.HISTOGRAM})) {
      // assuming the segments built from distribution are always right after the event, or 1 level deep
      // further breakdowns come after (but we would have hit the previous condition anyway)
      searchDepth = 1;
    }

    if (searchDepth) {
      return uniq(getKeysAtDepth(queryResults.series, searchDepth))
        .filter(
          // we don't support comparison to "All users" cohort yet, and we hide the "$overall" segment
          (segmentName) => segmentName !== TOP_PEOPLE.label && !segmentName.includes(RESULTS_OVERALL_SUM_KEY),
        )
        .map((segmentName: string) => {
          return {propertyValue: segmentName, displayName: segmentName};
        });
    }
  }

  return [];
}

export function getValueRepresentations(enableLiftComparison: boolean, displayOptions: DisplayOptions) {
  if (!isValueToggleEnabled(displayOptions) || enableLiftComparison) {
    return [];
  }
  return [ValueRepresentationType.Absolute, ValueRepresentationType.Relative];
}
